/**
 * 带过期时间和环境隔离的 Web Storage 封装
 * 支持 localStorage 和 sessionStorage
 *
 * 使用示例:
 * setStorageItem({ key: 'widget_shown', val: true, expireIn: 3 });
 * getStorageItem<boolean>('widget_shown');
 * removeStorageItem('widget_shown');
 */
import dayjs from 'dayjs';
import logger from './logger';

// 存储数据结构
interface StorageEntry<T = unknown> {
  exp: number | null;
  val: T;
}

// 存储类型
type StorageType = 'local' | 'session';

// setStorageItem 参数
interface SetStorageOptions<T = unknown> {
  /** 业务 key */
  key: string;
  /** 要存储的值 */
  val: T;
  /** 过期时间：数字表示天数，Date 表示具体时间，不传则永不过期 */
  expireIn?: Date | number;
  /** 存储类型，默认 localStorage */
  type?: StorageType;
  /** 业务前缀，默认 couponMall */
  prefix?: string;
}

// 默认业务前缀
const DEFAULT_PREFIX = 'couponMall';

// 获取当前环境标识
const getEnv = (): string => process.env.VUE_APP_ENV || 'unknown';

/**
 * 生成环境隔离的完整 key
 * 格式：prefix_key_env
 */
export const buildStorageKey = (key: string, prefix?: string): string => {
  const p = prefix || DEFAULT_PREFIX;
  return `${p}_${key}_${getEnv()}`;
};

// 获取对应的原生 Storage 实例
const getDriver = (type: StorageType): Storage | null => {
  try {
    return type === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

// 内存缓存
const cache = new Map<string, StorageEntry | null>();

// 判断是否过期
const isExpired = (exp: number | null): boolean => {
  if (exp === null || exp === undefined) return false;
  return Date.now() >= exp;
};

// 安全解析 JSON
const safeParse = (raw: string): StorageEntry | null => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'val' in parsed) {
      return parsed as StorageEntry;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * 删除存储值
 * @param key - 业务 key，会自动拼接环境前缀
 * @param type - 存储类型，'local' 使用 localStorage，'session' 使用 sessionStorage，默认 'local'
 * @param prefix - 业务前缀，用于 key 隔离，默认 'couponMall'
 */
export function removeStorageItem(
  key: string,
  type: StorageType = 'local',
  prefix?: string,
): void {
  const fullKey = buildStorageKey(key, prefix);
  cache.delete(fullKey);

  try {
    const driver = getDriver(type);
    driver?.removeItem(fullKey);
  } catch (err) {
    logger('[storage] removeItem 失败', err);
  }
}

/**
 * 读取存储值
 * @param key - 业务 key，会自动拼接环境前缀
 * @param type - 存储类型，'local' 使用 localStorage，'session' 使用 sessionStorage，默认 'local'
 * @param prefix - 业务前缀，用于 key 隔离，默认 'couponMall'
 * @returns 存储的值（泛型 T），过期或不存在返回 null
 */
export function getStorageItem<T = unknown>(
  key: string,
  type: StorageType = 'local',
  prefix?: string,
): T | null {
  const fullKey = buildStorageKey(key, prefix);

  try {
    if (cache.has(fullKey)) {
      const cached = cache.get(fullKey);
      if (!cached) return null;

      if (isExpired(cached.exp)) {
        removeStorageItem(key, type, prefix);
        return null;
      }
      return cached.val as T;
    }

    const driver = getDriver(type);
    if (!driver) return null;

    const raw = driver.getItem(fullKey);
    if (!raw) {
      cache.set(fullKey, null);
      return null;
    }

    const entry = safeParse(raw);
    if (!entry) {
      logger('[storage] 检测到脏数据，已清理', fullKey);
      driver.removeItem(fullKey);
      cache.set(fullKey, null);
      return null;
    }

    if (isExpired(entry.exp)) {
      removeStorageItem(key, type, prefix);
      return null;
    }

    cache.set(fullKey, entry);
    return entry.val as T;
  } catch (err) {
    logger('[storage] getItem 异常', err);
    return null;
  }
}

/**
 * 写入存储值
 * @param options - 写入配置对象
 * @param options.key - 业务 key，会自动拼接环境前缀
 * @param options.val - 要存储的值，支持任意可序列化类型
 * @param options.expireIn - 过期时间：number 表示天数（如 3 = 3天后过期），Date 表示具体过期时间，不传则永不过期
 * @param options.type - 存储类型，'local' 使用 localStorage，'session' 使用 sessionStorage，默认 'local'
 * @param options.prefix - 业务前缀，用于 key 隔离，默认 'couponMall'
 */
export function setStorageItem<T = unknown>(options: SetStorageOptions<T>): void {
  const {
    key,
    val,
    expireIn,
    type = 'local',
    prefix,
  } = options;

  if (val === undefined) return;

  let exp: number | null = null;
  if (expireIn instanceof Date) {
    exp = expireIn.getTime();
  } else if (typeof expireIn === 'number' && expireIn > 0) {
    exp = dayjs().add(expireIn, 'day').valueOf();
  }

  const entry: StorageEntry = { exp, val };
  const fullKey = buildStorageKey(key, prefix);
  cache.set(fullKey, entry);

  try {
    const driver = getDriver(type);
    if (driver) {
      driver.setItem(fullKey, JSON.stringify(entry));
    }
  } catch (err) {
    logger('[storage] setItem 失败', err);
  }
}

/**
 * 清除当前业务前缀下的所有存储（不影响其他模块）
 * @param type - 存储类型，'local' 使用 localStorage，'session' 使用 sessionStorage，默认 'local'
 * @param prefix - 业务前缀，只清除该前缀下的数据，默认 'couponMall'
 */
export function clearStorageByPrefix(
  type: StorageType = 'local',
  prefix?: string,
): void {
  const keyPrefix = `${prefix || DEFAULT_PREFIX}:${getEnv()}:`;

  try {
    const driver = getDriver(type);
    if (!driver) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < driver.length; i += 1) {
      const k = driver.key(i);
      if (k && k.startsWith(keyPrefix)) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach((k) => {
      driver.removeItem(k);
      cache.delete(k);
    });
  } catch (err) {
    logger('[storage] clearByPrefix 失败', err);
  }
}
